import React, { useState, useEffect, useCallback } from 'react';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { IUserJson } from '@scale/shared/types/user';
import {
  ChatBulkAuditBatch,
  SubtaskAssignment,
  SubtaskAssignmentAbbreviatedResponse,
  AttemptAuditResults,
} from '../SpeedAudit/types';
import ChatBulkAuditHeader from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditHeader';
import ChatBulkAuditPage from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditPage';
import { ScaleLogoLoading, HelperText } from '@scale/scaleui';
import {
  useAuditAttemptResponse,
  prefetchAuditAttemptResponse,
  useGetProjectRolesForUser,
} from 'react-scale/components/audits/ChatBulkAudit/apiClient';
import { makeStyles } from 'react-scale/components/design-system';
import _ from 'lodash';
import { useSkipAuditAttemptsStore } from 'components/audits/ChatBulkAudit/hooks/useSkipAuditAttemptStore';
import { useInitialResultPageRedirectStore } from 'components/audits/ChatBulkAudit/hooks/useInitialResultPageRedirectStore';
import { getAuditResultPageUrl } from 'components/audits/ChatBulkAudit/utils';
import { useAuditActions } from 'react-scale/components/ExpertManagement/stores/audit.store';
import { Role } from '@scale/shared/corp/projectRoles';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import { QAOperationContextProvider } from 'react-scale/components/remoAdmin/qaOperation/qaOperationContext';
import { QAOperationJson } from '@scale/shared/types/qaOperation/qaOperation';
import { getRelatedQaOperationForAuditBatch } from './ChatBulkAuditApi';
import { SquadMemberRole } from '@scale/shared/types/expertManagement';
import { useFetchUserInfo } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchUserInfo';
import { useGetAuditRubric } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClientAuditRubric';

interface ChatBulkAuditProps {
  auditBatchId?: string;
  user: IUserJson;
}

const useStyles = makeStyles(() => ({
  z100: {
    zIndex: 100,
  },
  loadingIcon: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    marginTop: '-50px',
    marginLeft: '-50px',
  },
}));

function ChatBulkAudit(props: ChatBulkAuditProps) {
  const [auditBatch, setAuditBatch] = useState<ChatBulkAuditBatch | null>(null);
  const [attemptAudits, setAttemptAudits] = useState<SubtaskAssignmentAbbreviatedResponse[]>([]);
  const [currentAttemptAudit, setCurrentAttemptAudit] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userHasProjectRole, setUserHasProjectRole] = useState<boolean>(false);
  const [userHasClaimTaskPerms, setUserHasClaimTaskPerms] = useState<boolean>(false);
  const [allowEdits, setAllowEdits] = useState<boolean>(false);
  const [formattedResponseView, setFormattedResponseView] = useState<boolean>(false);
  const [auditorHasClaim, setAuditorHasClaim] = useState<boolean>(false);
  const [relatedQaOperation, setRelatedQaOperation] = useState<QAOperationJson | undefined>(
    undefined,
  );
  const [showFeedbackBar, setShowFeedbackBar] = useState<boolean>(true);
  const setOpenChatBulkAuditResults = useAuditActions().setOpenChatBulkAuditResults;
  const initialPageRedirect = useInitialResultPageRedirectStore(
    state => state.initialResultPageRedirect,
  );
  const setInitialPageRedirect = useInitialResultPageRedirectStore(
    state => state.setInitialResultPageRedirect,
  );

  const classes = useStyles();

  const setSkipAuditAttemptIds = useSkipAuditAttemptsStore(state => state.setSkipAuditAttemptIds);
  const { setSelectedProject, setAuditBatchId } = useRemoAdminCtx();

  const fetchRelatedQaOperation = async (auditBatchId: string) => {
    const result = await getRelatedQaOperationForAuditBatch(auditBatchId);
    setRelatedQaOperation(result);
  };

  const fetchAuditBatch = async (auditBatchId: string) => {
    const fetchAuditBatchPromise = apiFetch(null, `corp-api/chatBulkAudit/batch/${auditBatchId}`, {
      method: 'GET',
    }).then((result): ChatBulkAuditBatch => {
      setAuditBatch(result);
      setAuditBatchId(result._id);
      setSelectedProject(result.project ?? '');
      setAuditorHasClaim(result.claimTasks);
      if (result.completed) {
        const urlParams = new URLSearchParams(window.location.search);
        const skipRedirect = urlParams.get('skipRedirect');
        if (window.location.pathname.includes('corp') && !skipRedirect) {
          window.location.replace(getAuditResultPageUrl(result._id) as string);
        } else if (!window.location.pathname.includes('corp') && initialPageRedirect) {
          setOpenChatBulkAuditResults(true);
          setInitialPageRedirect(false);
        }
      }
      return result;
    });

    const fetchAttemptAuditsPromise = apiFetch(
      null,
      `corp-api/chatBulkAudit/attemptAudit/${auditBatchId}`,
      {
        method: 'GET',
      },
    ).then(audits => {
      setAttemptAudits(audits);
      setSkipAuditAttemptIds(
        audits
          .filter((audit: AttemptAuditResults) => audit.ignored)
          .map((audit: AttemptAuditResults) => audit._id),
      );
      return;
    });
    await Promise.all([fetchAuditBatchPromise, fetchAttemptAuditsPromise]).then(promiseResults => {
      setIsLoading(false);
      const auditBatchResult: ChatBulkAuditBatch = promiseResults[0] as ChatBulkAuditBatch;
      if (auditBatchResult.attemptAudits.length > 1) {
        prefetchAuditAttemptResponse(auditBatchResult.attemptAudits[1]);
      }
      return;
    });
  };
  // TODO refresh this when the edits are undone - something to think about, what if two people are editing at the same time?
  const {
    data,
    isError,
    isLoading: isLoadingSubtaskAssignment,
  } = useAuditAttemptResponse(
    auditBatch?.attemptAudits?.[currentAttemptAudit],
    { staleTime: 60 * 60 * 1000 }, // 1 hour
  );
  const setCurrentAttemptAuditForAuditSelection = useCallback(
    (index: number) => {
      setCurrentAttemptAudit(index);
      if (auditBatch && auditBatch.attemptAudits && index + 1 < auditBatch.attemptAudits.length) {
        prefetchAuditAttemptResponse(auditBatch.attemptAudits[index + 1]);
      }
    },
    [auditBatch],
  );

  const populatedSubtaskAssignment = data as unknown as SubtaskAssignment;

  useEffect(() => {
    if (props.auditBatchId) {
      fetchAuditBatch(props.auditBatchId);
    } else {
      setIsLoading(false);
    }
  }, [props.auditBatchId]);

  const { data: projectRoleForUser } = useGetProjectRolesForUser(
    auditBatch?.project,
    props.user.id,
    [Role.EM, Role.STO, Role.DRI],
  );

  const { data: userInfo } = useFetchUserInfo(props.user.id, true);

  useEffect(() => {
    if (projectRoleForUser) {
      setUserHasProjectRole(true);
    }
  }, [projectRoleForUser]);

  useEffect(() => {
    if (
      userInfo?.squadRole === SquadMemberRole.TeamLead ||
      userInfo?.squadRole === SquadMemberRole.TeamLeadCoordinator
    ) {
      setUserHasClaimTaskPerms(true);
    } else {
      setUserHasClaimTaskPerms(false);
    }
  }, [userInfo]);

  useEffect(() => {
    if (props.auditBatchId) {
      fetchRelatedQaOperation(props.auditBatchId);
    }
  }, [props.auditBatchId]);

  // Rubric is determined at batch creation time and stored on the batch
  const { data: rubric, isLoading: isLoadingRubric } = useGetAuditRubric(auditBatch?.auditRubric);

  return (
    <div className="scaleui scale-tailwind scale-theme">
      {isError && !isLoading && (
        <HelperText className={'fixed top-12 left-0 ' + classes.z100} error>
          There was an error loading this task attempt. Please refresh the page, or contact support
          if that doesn't work.
        </HelperText>
      )}
      {!auditBatch ||
      (isLoadingRubric && !!auditBatch?.auditRubric) ||
      isLoading ||
      isLoadingSubtaskAssignment ? (
        <div className={'rounded-lg ' + classes.loadingIcon + ' ' + classes.z100}>
          <ScaleLogoLoading size="lg" />
        </div>
      ) : (
        <div className="fixed top-0 left-0 w-screen h-screen bg-neutral-0 z-50 overflow-y-auto">
          <div className="flex flex-col h-full">
            <ChatBulkAuditHeader
              auditBatch={auditBatch}
              auditorHasClaim={auditorHasClaim}
              allowEdits={allowEdits}
              setAllowEdits={setAllowEdits}
              formattedResponseView={formattedResponseView}
              setFormattedResponseView={setFormattedResponseView}
              attemptAudit={attemptAudits[currentAttemptAudit]}
              populatedSubtaskAssignment={populatedSubtaskAssignment}
              setShowFeedbackBar={setShowFeedbackBar}
            />
            {!_.isEmpty(attemptAudits) && (
              <QAOperationContextProvider
                initialQAOperation={relatedQaOperation}
                attemptResult={attemptAudits}
              >
                <ChatBulkAuditPage
                  showFeedbackBar={showFeedbackBar}
                  setShowFeedbackBar={setShowFeedbackBar}
                  currentAttemptAudit={currentAttemptAudit}
                  auditBatch={auditBatch}
                  rubric={rubric}
                  subtaskAssignment={populatedSubtaskAssignment}
                  subtaskAssignmentsLocalCopy={attemptAudits}
                  setSelectedAnnotation={setCurrentAttemptAuditForAuditSelection}
                  user={props.user}
                  userHasProjectRole={userHasProjectRole}
                  userHasClaimTaskPerms={userHasClaimTaskPerms}
                  allowEdits={allowEdits}
                  formattedResponseView={formattedResponseView}
                  auditorHasClaim={auditorHasClaim}
                />
              </QAOperationContextProvider>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(ChatBulkAudit);
